import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { getIconForType } from '@/lib/iconMapping';
import { integrationService } from '@/lib/api/services';
import { useApi } from '@/hooks/useApi';
import type { Integration } from '@/lib/types';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { GalleryGridSkeleton, GenericError } from '@/components/common';
import {
  LayoutGrid,
  Star,
  GitBranchPlus,
  Database,
  BrainCircuit,
  Code2,
  Search,
  Flower,
  ShieldCheck,
  Users,
  Box,
  ArrowRight
} from 'lucide-react';

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

const NodeCard = ({ node }: { node: Integration }) => {
  const iconComponent = React.useMemo(() => {
    try {
      const icon = getIconForType(node.iconType);
      return icon;
    } catch (error) {
      console.error('Error getting icon for type:', node.iconType, error);
      return <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center text-purple-600">🔌</div>;
    }
  }, [node.iconType]);

  return (
    <Link to={`/integrations/${node.id}`}>
      <div className="node-card bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-violet-300 transition-all flex items-start text-left gap-4 h-full">
        <div className="flex-shrink-0">
          {iconComponent}
        </div>
        <div className="flex-1">
          <h4 className="node-title font-semibold text-slate-800">{node.title}</h4>
          <p className="node-description text-slate-500 text-sm mt-1">{node.description}</p>
        </div>
      </div>
    </Link>
  );
};

const NavLink = ({ pageId, activePage, onClick, icon: Icon, children }: { pageId: string; activePage: string; onClick: (id: string) => void; icon?: React.ElementType; children: React.ReactNode }) => {
  const isActive = activePage === pageId;
  return (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onClick(pageId);
      }}
      className={`nav-link flex items-center gap-3 font-semibold py-2 pl-4 pr-4 -ml-4 rounded-md text-md ${
        isActive ? 'active bg-violet-600 text-white' : 'hover:text-violet-600 hover:bg-violet-50'
      }`}>
      {Icon && <Icon className="w-5 h-5 nav-icon" />} {children}
    </a>
  );
};


const Page = ({ title, description, nodes }: { title: string; description: string; nodes: Integration[] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const filteredNodes = searchTerm
    ? nodes.filter(
        (node) =>
          node.title.toLowerCase().includes(searchTerm.toLowerCase()) || node.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : nodes;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
      <h1 className="flex items-center gap-4 text-4xl font-bold text-slate-900 mb-2">
        {title}
      </h1>
      <p className="text-slate-600 mb-8">{description}</p>
      <div className="relative w-full mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          type="text"
          placeholder={`Search in ${title}...`}
          className="search-input w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-300 focus:outline-none"
          value={searchTerm}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
        {filteredNodes.map((node) => (
          <NodeCard key={node.id} node={node} />
        ))}
      </div>
    </div>
  );
};

const AllNodesPage = ({ integrations }: { integrations: Integration[] }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredIntegrations = searchTerm
    ? integrations.filter(
        (integration) =>
          integration.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          integration.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          integration.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : integrations;

  // Group integrations by category
  const groupedIntegrations = filteredIntegrations.reduce((acc, integration) => {
    const category = integration.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(integration);
    return acc;
  }, {} as Record<string, Integration[]>);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
      <h1 className="text-4xl font-bold text-slate-900 mb-2">All Nodes</h1>
      <p className="text-slate-600 mb-8">Browse the complete library of available nodes and integrations.</p>
      <div className="relative w-full mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          type="text"
          placeholder="Search all nodes..."
          className="search-input w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-300 focus:outline-none"
          value={searchTerm}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
        />
      </div>
      {Object.entries(groupedIntegrations).map(([category, nodes]) => (
        <section className="mb-12" key={category}>
          <h2 className="text-2xl font-bold text-slate-800 mb-4">{category}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
            {nodes.map((node) => (
              <NodeCard key={node.id} node={node} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

const HeroSection = () => (
  <header className="integration-hero text-left border-b border-slate-200 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white relative overflow-hidden">
    <div className="p-20 max-w-screen-2xl mx-auto">
      <h1 className="text-5xl md:text-6xl font-extrabold text-slate-900 mb-4">Nodes & Integrations</h1>
      <p className="max-w-3xl pt-4 text-xl text-black mb-8">
        Explore our library of nodes and integrations. Drag, drop, and connect your workflow to the world!
      </p>
      <div className="flex flex-wrap gap-4">
        <Button size="lg" className="bg-violet-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:bg-violet-700 transition">
          Get Started <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <Link to="/gallery/workflows">
          <Button
            size="lg"
            variant="outline"
            className="bg-white text-violet-600 font-semibold py-3 px-6 rounded-lg border hover:bg-violet-100 transition">
            See Workflow Gallery <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
        <Link to="/gallery/flows">
          <Button
            size="lg"
            variant="outline"
            className="bg-white text-violet-600 font-semibold py-3 px-6 rounded-lg border hover:bg-violet-100 transition">
            See Flow Store <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </div>
    </div>
  </header>
);

function FeaturedPage({ nodes }: { nodes: Integration[] }) {
  return (
    <Page
      title="Featured Nodes"
      description="A curated list of popular and powerful nodes to get you started quickly."
      nodes={nodes}
    />
  );
}

export default function Integrations() {
  const [activePage, setActivePage] = useState('featured-page');

  // Fetch integrations from API
  const integrationsResult = useApi(integrationService.list, [], { showErrorToast: true });

  const handleNavClick = useCallback((pageId) => {
    setActivePage(pageId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const { integrations = [], categories, featuredNodes } = useMemo(() => {
    if (!integrationsResult.data) {
      return { integrations: [], categories: {}, featuredNodes: [] };
    }

    const integrations = integrationsResult.data.data;
    const featuredNodes = integrations.filter((node) => node.featured);

    // Group by category
    const categories = integrations.reduce((acc, integration) => {
      const category = integration.category;
      if (!acc[category]) {
        acc[category] = {
          title: category,
          description: `Connect to ${category.toLowerCase()} services and tools.`,
          nodes: []
        };
      }
      acc[category].nodes.push(integration);
      return acc;
    }, {} as Record<string, { title: string; description: string; nodes: Integration[] }>);


    return { integrations, categories, featuredNodes };
  }, [integrationsResult.data]);


  const renderActivePage = () => {
    if (integrationsResult.loading) {
      return <GalleryGridSkeleton />;
    }

    if (integrationsResult.error) {
      return <GenericError onRetry={() => integrationsResult.refetch()} />;
    }

    switch (activePage) {
      case 'featured-page':
        return <FeaturedPage nodes={featuredNodes} />;
      case 'all-nodes-page':
        return <AllNodesPage integrations={integrations} />;
      default:
        const pageKey = activePage.replace('-page', '');
        const categoryKey = pageKey.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const pageData = categories[categoryKey];
        return pageData ? (
          <Page title={pageData.title} description={pageData.description} nodes={pageData.nodes} />
        ) : null;
    }
  };

  const activeComponent = renderActivePage();
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50 dark:from-slate-900 dark:to-violet-900">
      <Header currentPage="integrations" variant="gallery" />
      
      {/* Hero Section - Always at top */}
      <HeroSection />
      
      {/* Main Content Area */}
      <div className="bg-gradient-to-b from-slate-100 to-slate-50">
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="w-full lg:w-80 bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <nav className="space-y-1 text-slate-600">
                <NavLink pageId="featured-page" activePage={activePage} onClick={handleNavClick} icon={Star}>
                  Featured
                </NavLink>
                <NavLink pageId="all-nodes-page" activePage={activePage} onClick={handleNavClick} icon={LayoutGrid}>
                  All Nodes
                </NavLink>
                {Object.keys(categories).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2 mt-4">Categories</h3>
                    {Object.entries(categories).map(([categoryKey, categoryData]) => {
                      const pageId = categoryKey.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') + '-page';
                      const getIconForCategory = (category: string) => {
                        switch (category) {
                          case 'Core & Control Flow': return GitBranchPlus;
                          case 'Data Handling': return Database;
                          case 'AI & Machine Learning': return BrainCircuit;
                          case 'Developer Tools': return Code2;
                          default: return Box;
                        }
                      };
                      return (
                        <NavLink 
                          key={categoryKey}
                          pageId={pageId} 
                          activePage={activePage} 
                          onClick={handleNavClick} 
                          icon={getIconForCategory(categoryKey)}
                        >
                          {categoryKey}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </nav>
            </aside>

            {/* Content Area */}
            <main className="flex-1">
              {renderActivePage()}
            </main>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
